import { FormHelper } from "@/helpers/formHelper";
import { AdressDataModel } from "@/models/AdressResulModel";
import { PostalCodeValidator } from "@/validators/PostalCodeValidator";
import { Component, forwardRef, Input, OnDestroy } from "@angular/core";
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  UntypedFormBuilder,
  ValidationErrors,
  Validator,
  Validators,
} from "@angular/forms";
import { Subject, takeUntil } from "rxjs";

import { NgIf } from "@angular/common";
import { InputComponent } from "../input/input.component";

export interface Address {
  street: string;
  complement: string;
  postalCode: string;
  city: string;
  country: string;
  location: AddressLocation;
}
export interface AddressLocation {
  latitude: number;
  longitude: number;
  altitude: number;
}

@Component({
  selector: "app-address-form",
  templateUrl: "./address.component.html",
  styleUrls: ["./address.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AddressFormComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => AddressFormComponent),
      multi: true,
    },
  ],
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, NgIf, InputComponent],
})
export class AddressFormComponent
  implements ControlValueAccessor, Validator, OnDestroy
{
  @Input() legendDetails: string;

  destroySubject = new Subject<void>();

  get form_street() {
    return this.form.get("street") as FormControl<string | null>;
  }
  get form_complement() {
    return this.form.get("complement") as FormControl<string | null>;
  }
  get form_postalCode() {
    return this.form.get("postalCode") as FormControl<string | null>;
  }
  get form_city() {
    return this.form.get("city") as FormControl<string | null>;
  }
  get form_country() {
    return this.form.get("country") as FormControl<string | null>;
  }

  get form_location() {
    return this.form.get("location") as FormControl<AddressLocation | null>;
  }
  get form_latitude() {
    return this.form_location.value.latitude as number | null;
  }
  get form_longitude() {
    return this.form_location.value.longitude as number | null;
  }
  get form_altitude() {
    return this.form_location.value.altitude as number | null;
  }

  form = this.fb.group({
    street: [null, [Validators.required, Validators.maxLength(255)]],
    complement: [null, [Validators.maxLength(255)]],
    postalCode: [null, [Validators.required, PostalCodeValidator]],
    city: [null, [Validators.required, Validators.maxLength(255)]],
    country: [null, [Validators.maxLength(255)]],
    location: {
      latitude: [null],
      longitude: [null],
      altitude: [null],
    },
  });

  constructor(private fb: UntypedFormBuilder) {}

  onTouch() {}

  registerOnChange(fn: any): void {
    this.form.valueChanges.pipe(takeUntil(this.destroySubject)).subscribe(fn);
  }

  registerOnTouched(fn: any): void {
    this.form.valueChanges.pipe(takeUntil(this.destroySubject)).subscribe(fn);
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.form.disable() : this.form.enable();
  }

  writeValue(address: Address): void {
    this.form.patchValue(address, { emitEvent: false });
  }

  validate(control: AbstractControl): ValidationErrors | null {
    return this.isValid() ? null : { address: true };
  }

  isValid(): boolean {
    return this.form.valid || FormHelper.allFormIsEmpty2(this.form);
  }

  ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();
  }

  onClickOption(addressData: AdressDataModel) {
    this.form.patchValue({
      street: addressData.street,
      postalCode: addressData.postalCode,
      city: addressData.city,
      country: addressData.country,
      location: {
        latitude: addressData.latitude,
        longitude: addressData.longitude,
      },
    });
  }
}
